import styled, { css, keyframes } from 'styled-components';
import { rgba } from 'polished';
import colors from '../../../constants/colors';

import icClose from '../../../assets/icons/ic_close_white.svg';
import logo from '../../../assets/icons/logo_white.svg';

import { Background } from '../styles';
import BadgeGrey from '../../Badges/styles';

const MOBILE = '@media screen and (max-width: 992px)';
const PHONE = '@media screen and (max-width: 600px)';

// Darker than colors.main.green so the "Completed" label passes contrast on white.
const COMPLETED_GREEN = '#2E8A4A';
const BODY_GREY = '#4A525C';
const CONDENSED = `'DINNextW01-Condensed', Arial, sans-serif`;

// The modal and its backdrop come in together: a short fade plus a small rise on
// desktop, and a sheet that slides up from the bottom edge on phones. Every
// animation lands on the element's natural transform so nothing snaps into
// place when it finishes.
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const modalIn = keyframes`
  from {
    opacity: 0;
    transform: translate3d(0, 12px, 0) scale(0.98);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

const sheetIn = keyframes`
  from {
    opacity: 0;
    transform: translate3d(0, 32px, 0);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

const sectionLabel = css`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

export const BackgroundStyled = styled(Background)`
  animation-duration: 200ms;
  animation-timing-function: ease-out;
  animation-fill-mode: both;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 120ms;
  }
`;

// In normal flow rather than absolutely centred, so the overlay scrolls when the
// modal is taller than the screen. min-height keeps a short modal centred.
export const ModalWrapper = styled.div`
  position: relative;
  z-index: 11;
  display: flex;
  align-items: center;
  width: 100vw;
  max-width: 720px;
  min-height: 100%;
  padding: 40px 0;
  /* Clicks around the modal fall through to the backdrop, which closes it. */
  pointer-events: none;

  /* The shared modal shell goes full-screen at this breakpoint, so the sheet must too. */
  ${MOBILE} {
    align-items: flex-start;
    max-width: none;
    padding: 0;
  }
`;

export const Modal = styled.div`
  background-color: ${colors.main.white};
  width: 100%;
  flex-shrink: 0;
  pointer-events: auto;
  border-radius: 4px;
  overflow: hidden;
  will-change: transform, opacity;
  animation: ${modalIn} 260ms cubic-bezier(0.16, 1, 0.3, 1) both;

  ${MOBILE} {
    min-height: 100%;
    border-radius: 0;
    animation: ${sheetIn} 280ms cubic-bezier(0.16, 1, 0.3, 1) both;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: ${fadeIn} 120ms ease-out both;
  }
`;

// A short banner only: the title sits on white below it, so a long session name
// never has to fit over the photo.
export const Banner = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  height: 128px;
  padding: 20px 32px;
  background-position: 50% 35%;
  background-size: cover;
  background-repeat: no-repeat;
  ${(props) =>
    props.image
      ? css`
          background-image: url('${props.image}');
        `
      : css`
          background-color: ${colors.main.lightBlue};
        `}

  &:before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      45deg,
      ${rgba(colors.main.ruby, 0.55)} 16%,
      ${rgba(colors.main.blue, 0.55)} 64%
    );
  }

  ${PHONE} {
    height: 104px;
    padding: 16px 20px;
  }
`;

export const Logo = styled.div`
  position: relative;
  width: 84px;
  height: 42px;
  background: url('${logo}') left center / contain no-repeat;
`;

export const CloseIcon = styled.button`
  position: relative;
  width: 44px;
  height: 44px;
  margin: -4px -10px 0 0;
  padding: 0;
  border: 0;
  background: url(${icClose}) center center / 22px 22px no-repeat;
  cursor: pointer;
  border-radius: 50%;
  transition: background-color 150ms ease-out;

  &:hover {
    background-color: ${rgba(colors.main.white, 0.16)};
  }

  &:focus-visible {
    outline: 2px solid ${colors.main.white};
    outline-offset: 2px;
  }
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 0 40px 40px;

  ${PHONE} {
    gap: 28px;
    padding: 0 20px 28px;
  }
`;

export const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const IntroTop = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 20px;
  margin-top: -56px;

  ${PHONE} {
    gap: 14px;
    margin-top: -40px;
  }
`;

export const Thumbnail = styled.img`
  position: relative;
  flex-shrink: 0;
  width: 132px;
  height: 132px;
  object-fit: cover;
  border-radius: 12px;
  border: 4px solid ${colors.main.white};
  background: ${colors.main.disabledBtns};
  box-shadow: 0 4px 16px ${rgba(colors.main.black, 0.12)};

  ${PHONE} {
    width: 96px;
    height: 96px;
    border-radius: 10px;
  }
`;

export const IntroLabels = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
  padding-bottom: 6px;
`;

export const ProgramName = styled.span`
  ${sectionLabel};
  font-size: 14px;
  color: ${colors.main.black};
`;

export const Completed = styled.span`
  ${sectionLabel};
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${COMPLETED_GREEN};

  svg {
    flex-shrink: 0;
  }
`;

export const Badges = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

export const Badge = styled(BadgeGrey)`
  margin: 0;
  font-size: 11px;
  height: auto;
  padding: 3px 8px;
  background: ${colors.main.grey};

  ${MOBILE} {
    margin: 0;
  }
`;

// Long session names wrap to three lines at most and end in an ellipsis.
export const Title = styled.h1`
  margin: 0;
  font-family: ${CONDENSED};
  font-size: 40px;
  font-weight: 400;
  line-height: 1.05;
  text-transform: uppercase;
  color: ${colors.main.black};
  text-wrap: balance;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  ${PHONE} {
    font-size: 30px;
  }
`;

export const Meta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 16px;
  line-height: 1.4;
  color: ${colors.main.grey};
`;

export const Totals = styled.p`
  margin: 0;
  font-size: 18px;
  color: ${colors.main.black};

  strong {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const SectionTitle = styled.h2`
  ${sectionLabel};
  margin: 0;
  font-size: 14px;
  color: ${colors.main.black};
`;

export const RatingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${(props) => props.count}, minmax(0, 1fr));
  gap: 12px;

  ${PHONE} {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
  }
`;

export const RatingTile = styled.div`
  container-type: inline-size;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 18px;
  border: 1px solid ${colors.main.disabledBtns};
  border-radius: 10px;

  ${PHONE} {
    display: grid;
    grid-template-columns: 72px minmax(0, 1fr);
    column-gap: 12px;
    row-gap: 2px;
    align-items: baseline;
    padding: 12px 16px;
  }
`;

export const RatingLabel = styled.span`
  ${sectionLabel};
  font-weight: 600;
  color: ${colors.main.grey};
`;

// One word per rating, never split across lines: it scales down with the tile
// instead, so the longest option ("Mechanical") fits three tiles across.
export const RatingValue = styled.span`
  font-family: ${CONDENSED};
  font-size: 28px;
  font-size: clamp(20px, 19cqi, 32px);
  line-height: 1.1;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${colors.main.black};

  ${PHONE} {
    font-size: 26px;
  }
`;

export const RatingDescription = styled.span`
  font-size: 14px;
  line-height: 1.4;
  color: ${BODY_GREY};

  ${PHONE} {
    grid-column: 2;
  }
`;

export const Reflection = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.main.disabledBtns};
  border-radius: 10px;

  & > * + * {
    border-top: 1px solid ${colors.main.dividers};
  }
`;

export const Pick = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
`;

export const PickImage = styled.img`
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  object-fit: cover;
  border-radius: 6px;
  background: ${colors.main.disabledBtns};
`;

export const PickText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const PickLabel = styled.span`
  ${sectionLabel};
  font-weight: 600;
  color: ${colors.main.grey};
`;

export const PickName = styled.span`
  font-size: 17px;
  font-weight: 700;
  color: ${colors.main.black};
`;

export const Notes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
`;

export const Tags = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const Tag = styled.li`
  padding: 4px 12px;
  border: 1px solid ${colors.main.disabledBtns};
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.main.black};
`;

export const NoteText = styled.p`
  margin: 0;
  max-height: 150px;
  overflow-y: auto;
  overscroll-behavior: contain;
  white-space: pre-line;
  overflow-wrap: anywhere;
  font-size: 16px;
  line-height: 1.5;
  color: ${colors.main.black};
`;

export const UpNext = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: ${colors.main.secondaryBtns};
  border-radius: 10px;

  ${PHONE} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const UpNextText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const UpNextLabel = styled.span`
  ${sectionLabel};
  font-weight: 600;
  color: ${colors.main.grey};
`;

export const UpNextName = styled.span`
  font-size: 17px;
  font-weight: 700;
  line-height: 1.25;
  text-transform: uppercase;
  color: ${colors.main.black};
`;

export const UpNextHint = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
  color: ${BODY_GREY};
`;

export const UpNextActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;

  ${PHONE} {
    align-items: stretch;
  }
`;

export const PreviewLink = styled.a`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 0 20px;
  border-radius: 999px;
  background: ${colors.main.darkBlue};
  color: ${colors.main.white};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-decoration: none;
  white-space: nowrap;
  transition: background-color 150ms ease-out;

  &:hover {
    background: ${colors.main.royal};
  }

  &:focus-visible {
    outline: 2px solid ${colors.main.blue};
    outline-offset: 2px;
  }
`;

export const DashboardButton = styled.button`
  min-height: 44px;
  padding: 0 8px;
  border: 0;
  background: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: ${BODY_GREY};

  &:hover:not(:disabled) {
    color: ${colors.main.black};
  }

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: 2px solid ${colors.main.blue};
    outline-offset: 2px;
  }
`;
